import { AsyncPipe } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { Router } from '@angular/router';
import { AsmAuthService, AsmAuthStorageService, CsAgentAuthService } from '@spartacus/asm/root';
import {
  AuthService,
  AuthStorageService,
  ClientTokenService,
  CmsComponent,
  GlobalMessageService,
  GlobalMessageType,
  TranslatePipe,
} from '@spartacus/core';
import { CmsComponentData } from '@spartacus/storefront';
import { Observable, Subject, Subscription, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, take } from 'rxjs/operators';
import { DealerSelectorListPayload, DealerService, PaginationModel, Sorts } from 'src/app/service/asm/dealer.service';
import { ComponentService } from 'src/app/service/component.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { LogoutService } from 'src/app/service/user/logout.service';
import { ASM_ENABLED_LOCAL_STORAGE_KEY } from '../asm/asm-constant';
import { PaginationComponent } from '../pagination/pagination.component';
import { SortingComponent } from '../sorting/sorting.component';
import { DealerListComponent } from './dealer-list/dealer-list.component';

@Component({
  selector: 'app-dealer-selection-list',
  templateUrl: './dealer-selection-list.component.html',
  styleUrls: ['./dealer-selection-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    MatFormField,
    MatLabel,
    MatInput,
    ReactiveFormsModule,
    FormsModule,
    MatIcon,
    MatSuffix,
    SortingComponent,
    DealerListComponent,
    PaginationComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class DealerSelectionListComponent implements OnInit, OnDestroy {
  impersonation: any;
  dealerLists$: Observable<any>;
  asmMainUi = document.querySelector('cx-asm-main-ui');
  body = document.querySelector('body');
  sortOptions = [
    {
      code: '12',
      name: '12',
      selected: true,
    },
    {
      code: '24',
      name: '24',
      selected: false,
    },
    {
      code: '48',
      name: '48',
      selected: false,
    },
  ];
  sort = '12';
  initialPaginationModel: PaginationModel = {
    currentPage: 0,
    hasNext: true,
    hasPrevious: true,
    needsTotal: true,
    numberOfPages: 0,
    pageSize: 12,
    totalNumberOfResults: 0,
  };
  initialSorts: Sorts = {
    asc: true,
    code: 'name',
    name: 'name',
    selected: true,
  };
  showPagination = false;
  dealerServiceListPayload: DealerSelectorListPayload = {
    asmUserId: this.storageService.getLocalStorageItem('asmUserId'),
    searchPageData: {
      pagination: this.initialPaginationModel,
      results: [{}],
      sorts: [this.initialSorts],
      searchTerm: '',
    },
  };
  searchField: string;
  searchFieldChanged: Subject<string> = new Subject<string>();
  header = document.querySelector('header');
  globalMsg = document.querySelector('cx-global-message');
  redirectSubs = new Subscription();
  isShipTo$: Observable<boolean> = this.componentData.data$.pipe(
    map((data) => data.uid === 'DealerSelectionListShipToComponent')
  );

  constructor(
    private dealerService: DealerService,
    private cd: ChangeDetectorRef,
    private componentService: ComponentService,
    private asmAuthService: AsmAuthService,
    private logoutService: LogoutService,
    private authService: AuthService,
    private csAgentAuthService: CsAgentAuthService,
    private globalMessageService: GlobalMessageService,
    private asmAuthStorageService: AsmAuthStorageService,
    private authStorageService: AuthStorageService,
    private storageService: StorageService,
    protected clientToken: ClientTokenService,
    private router: Router,
    protected componentData: CmsComponentData<CmsComponent>
  ) {
    this.header.style.display = 'block';
    this.globalMessageService.remove(GlobalMessageType.MSG_TYPE_CONFIRMATION);
    this.globalMsg.setAttribute('style', 'display:block;');
    this.searchFieldChanged.pipe(debounceTime(300), distinctUntilChanged()).subscribe((searchField) => {
      this.searchField = searchField;
      this.dealerServiceListPayload.searchPageData.searchTerm = this.searchField;
      this.dealerServiceListPayload.searchPageData.pagination.currentPage = 0;
      this.loadData();
      this.cd.markForCheck();
    });
  }
  changed(text: string) {
    this.searchFieldChanged.next(text);
  }
  updateImpersonation(val: any) {
    this.impersonation = val;
  }

  loadData() {
    this.dealerLists$ = this.isShipTo$.pipe(
      take(1),
      switchMap((isShipTo: boolean) =>
        this.dealerService.getDealerSelectorList(this.dealerServiceListPayload, isShipTo)
      ),
      filter(Boolean),
      map((result: any) => {
        this.initialPaginationModel = {
          currentPage: result.pagination.page,
          hasNext: result.pagination.hasNext,
          hasPrevious: result.pagination.hasPrevious,
          needsTotal: result.pagination.needsTotal,
          numberOfPages: result.pagination.totalPages,
          totalPages: result.pagination.totalPages,
          pageSize: result.pagination.count,
          totalNumberOfResults: result.pagination.totalCount,
        };
        this.cd.detectChanges();
        return result;
      })
    );
  }

  ngOnInit() {
    this.loadData();
    this.asmMainUi.classList.add('dealer-page');
    this.body.classList.add('dealer-selection-page');

    if (!this.componentService.isPageLoadedFromLogin$.getValue()) {
      this.componentService.isPageLoadedFromLogin$.next(true);
      this.storageService.removeLocalStorageItem('selectedLanguageOnLogin');
    }

    this.redirectSubs = combineLatest([
      this.asmAuthStorageService.getToken(),
      this.authService.isUserLoggedIn(),
    ]).subscribe(([csaToken, isUserLoggedIn]) => {
      let isCustomerEmulated = this.asmAuthStorageService.getTokenTarget();

      if (!csaToken && isCustomerEmulated) {
        this.logoutService.logout();
        this.storageService.clearLocalStorageItem();
        this.storageService.setLocalStorageitem(ASM_ENABLED_LOCAL_STORAGE_KEY, 'true');
        this.router.navigate(['login'], { queryParams: { asm: 'true' } }).then(() => {
          if (!isUserLoggedIn) window.location.reload();
        });
      }
    });
  }

  ngOnDestroy() {
    this.asmMainUi.classList.remove('dealer-page');
    this.body.classList.remove('dealer-selection-page');
    this.redirectSubs.unsubscribe();
  }
  pageChange(page: any) {
    this.dealerServiceListPayload.searchPageData.pagination.currentPage = page;
    this.loadData();
  }
  sortChange(sort: any) {
    this.sort = sort;
    this.dealerServiceListPayload.searchPageData.pagination.pageSize = sort;
    this.dealerServiceListPayload.searchPageData.pagination.currentPage = 0;
    this.loadData();
  }

  hideDealerSelectionPage(add: boolean) {
    this.toggleClassName(document.getElementsByClassName('header')[0] as HTMLElement, 'd-none', add);
    this.toggleClassName(
      document.getElementsByClassName('DealerSelectionPageTemplate')[0] as HTMLElement,
      'd-none',
      add
    );
  }

  toggleClassName(element: HTMLElement, className: string, add: boolean) {
    if (element) {
      add ? element.classList.add(className) : element.classList.remove(className);
    }
  }
}
